import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { Dialog } from 'primereact/dialog';
import { toast } from 'react-toastify';
import Strings from '../../../i18n';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import { DeviceMethodsSideBarProps } from './deviceMethodsSideBarProps';
import {
  useDeleteDeviceMutation,
  useDeleteDeviceCredentialsMutation,
  useGetCredentialsMutation,
  usePostCustomFileDeviceMutation,
  useDeviceRebootCommandMutation
} from '../../../services/device/deviceService';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { SideBarDeleteActionWithPopup } from '../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import { SideBarSimpleMethodWithValue } from '../../../components/commons/SideBarComponents/MethodWithValueItem/sideBarMethodWithValueItem';
import { JsonViewItem } from '../../../components/commons/JsonViewItem/jsonViewItem';
import { useDeleteDeviceFromLocationMutation } from '../../../services/deviceByLocation/deviceByLocationService';

export const DeviceMethodsSideBar = ({
  openRightSideBarForMethod,
  deviceIds,
  reloadTable,
  setOpenRightSideBar,
  deviceLocationView,
  clearSingleDevice,
  idDevice
}: DeviceMethodsSideBarProps) => {
  const [visibleCredentials, setVisible] = React.useState(false);
  const [fileUpload, setFileUpload] = useState<any>(null);
  const [
    deleteCredentials,
    {
      isSuccess: isSuccessDeleteCredentials,
      isLoading: isLoadingDeleteCredentials
    }
  ] = useDeleteDeviceCredentialsMutation();
  const [
    getCredentials,
    {
      isLoading: isLoadingGetCredentials,
      isSuccess: isSuccessGetCredentials,
      data: dataGetCredentials
    }
  ] = useGetCredentialsMutation();
  const [deleteDevice, { isSuccess: isSuccessDeleted }] =
    useDeleteDeviceMutation();

  const [postCustomFile, { isSuccess: isSuccessPostCustom }] =
    usePostCustomFileDeviceMutation();

  const [rebootDevicesCommand, { isSuccess: isSuccessRebootDevice }] =
    useDeviceRebootCommandMutation();

  const [deleteFromLocation, { isSuccess: isSuccessDeleteFromLocation }] =
    useDeleteDeviceFromLocationMutation();

  useEffect(() => {
    if (isSuccessDeleted || isSuccessDeleteFromLocation) {
      toast.success(Strings.serverAccess.devices.successDeletedDevice);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessDeleted || isSuccessDeleteFromLocation]);

  useEffect(() => {
    if (isSuccessPostCustom) {
      toast.success(Strings.serverAccess.devices.successPostCustomFile);
      setFileUpload(null);
      setOpenRightSideBar(false);
    }
  }, [isSuccessPostCustom]);

  useEffect(() => {
    if (isSuccessRebootDevice) {
      toast.success(Strings.serverAccess.devices.successRebootDevice);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessRebootDevice]);

  const deleteItem = (id: string[]) => {
    deleteDevice(id);
  };

  const changeFileHandler = (event: any) => {
    setFileUpload(event.target.files[0]);
  };

  const sendFile = () => {
    if (fileUpload) {
      const data = new FormData();
      data.append('file', fileUpload);
      postCustomFile({ deviceId: deviceIds[0], data });
    }
  };

  const rebootDevices = (ids: string[]) => {
    rebootDevicesCommand(ids);
  };

  const getCredentialsAndOpenDialog = () => {
    setVisible(true);
    setOpenRightSideBar(false);
    getCredentials(deviceIds[0]);
  };
  useEffect(() => {
    if (isSuccessDeleteCredentials) {
      toast.success(Strings.serverAccess.devices.successDeleteCredentials);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessDeleteCredentials]);

  return (
    <>
      <Grid container>
        <SideBarSimpleMethodItem
          name={Strings.serverAccess.devices.singletonMethodLabel}
          action={() =>
            openRightSideBarForMethod(viewRightSideBarEnum.singletonDevice)
          }
          successAction={false}
          textSuccess="success"
        />
        <SideBarSimpleMethodItem
          name={Strings.serverAccess.devices.staticMethodLabel}
          action={() =>
            openRightSideBarForMethod(viewRightSideBarEnum.staticDevice)
          }
          successAction={false}
          textSuccess="success"
        />
        {!deviceLocationView && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.addDeviceToLocationLabel}
            action={() => {
              clearSingleDevice();
              openRightSideBarForMethod(
                viewRightSideBarEnum.addDeviceToLocation
              );
            }}
            successAction={false}
            textSuccess="success"
          />
        )}

        {deviceIds.length === 1 && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.getCredentials}
            action={() => getCredentialsAndOpenDialog()}
            successAction={false}
            textSuccess="success"
          />
        )}
        {deviceIds.length === 1 && (
          <SideBarSimpleMethodItem
            name={Strings.notes.addNote}
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.addNote);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {deviceIds.length === 1 && (
          <SideBarSimpleMethodWithValue
            label="Custom File"
            type="inputFile"
            currentValue={fileUpload}
            onChange={(e) => changeFileHandler(e)}
            action={() => sendFile()}
            successAction
            textSuccess="success"
            fileName={fileUpload?.name ?? ''}
          />
        )}
        {deviceLocationView && (
          <SideBarDeleteActionWithPopup
            onDelete={() => deleteFromLocation(idDevice)}
            titleConfirmation={
              Strings.serverAccess.devices.deleteDeviceFromLocationTitle
            }
            textConfirmation={
              Strings.serverAccess.devices.deleteDeviceFromLocation
            }
            textIcon={Strings.devices.deleteAssignedLocation}
          />
        )}
        {!deviceLocationView && (
          <SideBarDeleteActionWithPopup
            onDelete={() => deleteItem(deviceIds)}
            titleConfirmation={Strings.serverAccess.devices.deleteDeviceTitle}
            textConfirmation={Strings.serverAccess.devices.deleteDeviceText}
          />
        )}
        {deviceLocationView && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.rebootDevicesLabel}
            action={() => rebootDevices(deviceIds)}
            successAction={false}
            textSuccess="success"
          />
        )}
        {deviceLocationView && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.setWifiCredentialsLabel}
            action={() => {
              openRightSideBarForMethod(
                viewRightSideBarEnum.SetWifiCredentials
              );
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {deviceIds.length === 1 && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.deleteCredentials}
            action={() => {
              deleteCredentials(deviceIds[0]);
            }}
            successAction={false}
            textSuccess="success"
            loading={isLoadingDeleteCredentials}
          />
        )}
      </Grid>
      <Dialog
        header="Credentials"
        visible={visibleCredentials}
        style={{ width: '65vw', overflow: '999' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => setVisible(false)}
      >
        {isLoadingGetCredentials && <LoadingRowsComponent />}
        {isSuccessGetCredentials &&
          dataGetCredentials &&
          dataGetCredentials.map((item, index) => (
            <JsonViewItem
              key={index}
              title={item.split(' : ')[0]}
              values={item.split(' : ')[1]}
            />
          ))}
      </Dialog>
    </>
  );
};
DeviceMethodsSideBar.defaultProps = {
  clearSingleDevice: () => {}
};
